import { ExternalLink } from "lucide-react"
import { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"

import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Skeleton } from "@/components/ui/skeleton"
import { useCurrentContext } from "@/hooks/use-current-context"
import { useInitials } from "@/hooks/use-initials"
import { cn } from "@/lib/utils"
import type {
  ConversationDetail,
  ConversationDetailMembership,
  ThreadMessage,
} from "@/types/messaging"

import { MessageComposer } from "./message-composer"
import {
  MessageThreadScroller,
  type MessageThreadScrollerHandle,
} from "./message-thread-scroller"

/**
 * OKS-37 — Brand-side thread detail (right pane).
 *
 * Renders:
 *   - Header: avatar + name + handle + "View profile" link
 *   - Messages list (oldest → newest, date separators)
 *   - Optional paused/archived banner
 *   - Composer (when `canReply` is true)
 *
 * Accessibility:
 *   - Messages region is `role="log"` `aria-live="polite"` so AT
 *     announces new bubbles arriving via poll. UX Spec §6.
 *   - Focus moves to composer when this pane mounts (UX Spec §6).
 */
interface ThreadPaneProps {
  conversation: ConversationDetail | null
  membership: ConversationDetailMembership
  messages: ThreadMessage[]
  canReply: boolean
  composerLimit: number
  /** URL the composer POSTs to. */
  sendUrl: string
  className?: string
}

export function ThreadPane({
  conversation,
  membership,
  messages,
  canReply,
  composerLimit,
  sendUrl,
  className,
}: ThreadPaneProps) {
  const { t } = useTranslation()
  const initials = useInitials()
  const composerRef = useRef<HTMLTextAreaElement | null>(null)
  // OKS-724 — held by the thread scroller; the composer forces the thread to
  // the newest message here on a successful own-send.
  const scrollerRef = useRef<MessageThreadScrollerHandle>(null)
  // OKS-312 — While the OKS Admin is in act-as context, `canReply`
  // returns `false` from the server so the composer is suppressed.
  // We render an explicit helper-text strip in its place (UX Spec §3
  // edge — "disabled-with-explanation" beats silent disappearance).
  // The server STILL hard-blocks send via 404 (`BrandMessagesController
  // #reject_when_acting_as!`) — this is UX clarity, not the gate.
  const { actingAs } = useCurrentContext()
  const showActingAsBlock = !canReply && actingAs !== null

  // Focus composer on mount (UX Spec §6 a11y). Auto-scroll to newest
  // is now owned by the `MessageScroller` primitive (`autoScroll`).
  useEffect(() => {
    composerRef.current?.focus({ preventScroll: true })
  }, [membership.id])

  const isPaused = membership.status === "paused"
  const isArchived = membership.status === "archived"
  const showBanner = isPaused || isArchived

  return (
    <section
      className={cn(
        "bg-background flex h-full min-w-0 flex-1 flex-col",
        className,
      )}
      data-testid="messaging-thread-pane"
      aria-label={`Conversation with ${membership.creatorDisplayName}`}
    >
      <header className="border-border flex items-center gap-3 border-b p-3">
        <Avatar className="size-9">
          <AvatarFallback>
            {initials(membership.creatorDisplayName)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">
            {membership.creatorDisplayName}
          </p>
          {membership.creatorTiktokHandle && (
            <p className="text-muted-foreground truncate text-xs">
              @{membership.creatorTiktokHandle}
            </p>
          )}
        </div>
        <a
          href={membership.creatorProfileUrl}
          aria-label={t("messaging.brand.thread.view_profile_aria")}
          className="text-muted-foreground hover:bg-accent hover:text-foreground rounded p-2"
          data-testid="messaging-thread-view-profile"
        >
          <ExternalLink className="size-4" aria-hidden="true" />
        </a>
      </header>

      {showBanner && (
        <div
          className="border-border bg-muted text-muted-foreground border-b px-3 py-2 text-xs"
          data-testid="messaging-thread-paused-banner"
          role="status"
        >
          {/* Direct `t("literal-key")` per branch — i18n scanner only matches static-string args. */}
          {isArchived
            ? t("messaging.paused_banner.archived")
            : t("messaging.paused_banner.paused")}
        </div>
      )}

      {/*
          🔴 OKS-945 AC6/AC7 — STRUCTURE RECORDED, DELIBERATELY NO CODE CHANGE.
          The empty band immediately below, and `ThreadSkeleton` further down,
          both carry the OKS-723 irreducible-floor shape. Neither is changed
          here. Written down so the next reader does not re-derive this, and
          does not "fix" it either.

          THE STRUCTURE. This band is a flex child that declares no
          `min-height: 0`, and it carries `padding: 1.5rem` on the flex child
          itself rather than on an inner wrapper. That is the shape OKS-723
          recorded as MEASURED harmful on the creator column.

          🔴 AND IT IS NOT MANIFEST IN THE STATE WE MEASURED. That is the
          finding — not "not broken", and not "fixed". `min-height: auto` binds
          only when available space falls below content height, and here it
          never gets close. Measured at 375x568:

            568 = 322 shell/header chrome + 185 band + 61 composer
            empty-state content overflowing the band: -61px (i.e. 61px spare)

          CI run 30722707672 @ 8e499298 — Playwright is the only instrument
          that can see this, because jsdom does no layout and a height
          assertion there passes vacuously. Roughly 88px of content against
          185px of space is never under pressure.

          WHY THAT DISQUALIFIES THE FIX RATHER THAN DEFERRING IT. The
          instrument reads the same -61px with or without a `min-height: 0`
          declaration, so landing one would be invisible to every leg that
          exists — permanently unguarded, while reading as coverage in the
          diff. Same disqualifier that left the two unreachable creator
          branches alone in `pages/Creator/Messages/Index.tsx`. Note carefully
          what the disqualifier IS: not reachability (this band is reachable
          and renders), but that NO LEG CAN SEE THE CHANGE.

          THREE REACHABLE STATES EAT INTO THAT 185px, AND NONE IS MEASURED:
            - `showBanner` (`isPaused || isArchived`) renders a paused/archived
              row above this band;
            - `showActingAsBlock` (OKS-312, `!canReply && actingAs !== null`)
              renders a strip below the composer, inside the same column;
            - the FR empty-state copy is longer than EN and wraps further at
              this width.
          Each narrows the 61px of spare. If this surface is revisited, measure
          those first — the fix only becomes verifiable once one of them puts
          the band under actual pressure.

          ⚠️ REACHABILITY ASYMMETRY, AND IT IS NOT IN THIS COMPONENT. The
          condition below is `messages.length === 0`, the SAME condition ruled
          UNREACHABLE on the creator side. The brand reaches it with a NULL
          conversation: `brand_conversations_controller#show` serves
          `messages: []` with `current_conversation: nil` when a brand opens a
          membership that has no conversation yet (its own comment says so),
          and `currentMembership` is non-null, so this pane still renders. The
          creator path would need a conversation to EXIST holding zero
          messages, which `Conversations::CreateMessage` forbids by writing the
          conversation and its first message in one transaction. Two
          identical-looking conditions, opposite reachability — do not
          "harmonise" them.

          ⚠️ `ThreadSkeleton` BELOW IS DEAD CODE. `git grep -w` finds no import
          site for it anywhere in the repo, no barrel file in this directory,
          and no dynamic import — the only code occurrence is its own
          `export function` declaration. (Deliberately stated structurally
          rather than as a count: a count written here would have falsified
          itself the moment this comment named the symbol.) Positive control on
          the method: the sibling export `ThreadEmptyState` DOES resolve, to a
          real import and render in `pages/Crew/Messages/Index.tsx`. Nothing
          renders `ThreadSkeleton`, so a change to it is unverifiable by
          construction.

          ⚠️ EVERY TOKEN ABOVE IS SPELLED AS A CSS PROPERTY, NOT AS A UTILITY
          CLASS, ON PURPOSE. Tailwind scans comments: a bare utility string
          written here would emit that utility into the bundle, and would make
          "is this class present on this file?" return the same answer whether
          or not the class is actually applied — destroying exactly the
          forensic grep a later reader of this comment will want to run.
      */}
      {messages.length === 0 ? (
        <div
          role="log"
          aria-live="polite"
          aria-label={t("messaging.brand.messages_aria")}
          className="flex flex-1 items-center justify-center p-6 text-center"
          data-testid="messaging-thread-messages"
        >
          <div>
            <p className="text-sm font-medium">
              {t("messaging.brand.thread.empty_title")}
            </p>
            <p className="text-muted-foreground mt-1 text-xs">
              {t("messaging.brand.thread.empty_description")}
            </p>
          </div>
        </div>
      ) : (
        <div className="min-h-0 flex-1">
          <MessageThreadScroller
            ref={scrollerRef}
            messages={messages}
            ariaLabel={t("messaging.brand.messages_aria")}
            testId="messaging-thread-messages"
          />
        </div>
      )}

      {canReply && (
        <MessageComposer
          ref={composerRef}
          actionUrl={sendUrl}
          limit={composerLimit}
          conversationId={conversation?.id ?? null}
          onSuccess={() => scrollerRef.current?.scrollToNewest()}
        />
      )}

      {showActingAsBlock && (
        <div
          className="border-border bg-muted text-muted-foreground border-t px-3 py-3 text-xs"
          role="status"
          data-testid="messaging-thread-acting-as-block"
        >
          {t("messaging.composer.disabled_acting_as")}
        </div>
      )}
    </section>
  )
}

export function ThreadSkeleton() {
  const { t } = useTranslation()
  return (
    <div
      className="flex flex-1 flex-col gap-3 p-4"
      role="status"
      aria-live="polite"
      aria-label={t("messaging.brand.thread.loading")}
      data-testid="messaging-thread-skeleton"
    >
      <span className="sr-only">{t("messaging.brand.thread.loading")}</span>
      <Skeleton className="h-12 w-[40%] self-start rounded" />
      <Skeleton className="h-12 w-[50%] self-end rounded" />
      <Skeleton className="h-16 w-[55%] self-start rounded" />
    </div>
  )
}

export function ThreadEmptyState({ message }: { message: string }) {
  return (
    <div
      className="text-muted-foreground flex h-full flex-1 items-center justify-center p-8 text-center"
      data-testid="messaging-thread-empty-state"
    >
      <p className="text-sm">{message}</p>
    </div>
  )
}

/**
 * Error card for the thread pane (UX Spec §3 thread error state).
 * Caller passes an `onRetry` to wire `router.reload({ only: [...] })`
 * when the user hits retry; the component itself is presentational.
 */
export function ThreadErrorCard({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation()
  return (
    <div
      role="alert"
      className="flex h-full flex-1 items-center justify-center p-8 text-center"
      data-testid="messaging-thread-error-card"
    >
      <div className="space-y-3">
        <p className="text-destructive text-sm font-medium">
          {t("messaging.brand.thread.error_title")}
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="text-primary text-xs font-medium hover:underline"
          data-testid="messaging-thread-error-retry"
        >
          {t("messaging.brand.thread.error_retry")}
        </button>
      </div>
    </div>
  )
}
